import type { Tokens } from "next-firebase-auth-edge";

// Standard claims from Firebase ID token that should be filtered out
const STANDARD_CLAIMS = [
  "iss",
  "aud",
  "auth_time",
  "user_id",
  "sub",
  "iat",
  "exp",
  "email",
  "email_verified",
  "firebase",
  "uid",
  "name",
  "picture",
  "phone_number",
  "source_sign_in_provider",
];

// Custom claims type that your app might use
export interface CustomClaims {
  role?: string;
  clientId?: string;
  [key: string]: unknown;
}

// Impersonation state
export interface ImpersonationState {
  isImpersonating: boolean;
  impersonatedUserId?: string;
  impersonatedUserEmail?: string;
  impersonatedUserName?: string;
  impersonatedClientId?: string;
  /** Organization name for the read-only notices (AIR-1435); absent on cookies minted before it. */
  impersonatedClientName?: string;
  adminUid: string;
  adminEmail: string;
  startedAt: string;
}

export interface User {
  uid: string;
  email: string | null;
  emailVerified: boolean;
  displayName: string | null;
  photoURL: string | null;
  phoneNumber: string | null;
  providerId: string;
  customClaims: CustomClaims;
  impersonation?: ImpersonationState;
}

// Filter out standard claims to get only custom claims
function filterCustomClaims(
  decodedToken: Record<string, unknown>
): CustomClaims {
  const customClaims: CustomClaims = {};
  for (const [key, value] of Object.entries(decodedToken)) {
    if (!STANDARD_CLAIMS.includes(key)) {
      customClaims[key] = value;
    }
  }
  return customClaims;
}

// Helper function to map tokens to User object (server-safe)
export function toUser({ decodedToken }: Tokens): User {
  const {
    uid,
    email,
    picture: photoURL,
    email_verified: emailVerified,
    phone_number: phoneNumber,
    name: displayName,
    firebase,
  } = decodedToken;

  return {
    uid,
    email: email ?? null,
    displayName: displayName ?? null,
    photoURL: photoURL ?? null,
    phoneNumber: phoneNumber ?? null,
    emailVerified: emailVerified ?? false,
    providerId: firebase?.sign_in_provider ?? "unknown",
    customClaims: filterCustomClaims(decodedToken),
  };
}

export function isSuperAdmin(user: User | null | undefined): boolean {
  return user?.customClaims?.role === "super_admin";
}

export function isEnterpriseAdmin(user: User | null | undefined): boolean {
  return user?.customClaims?.role === "enterprise_admin";
}

export function hasAdminAccess(user: User | null | undefined): boolean {
  return isSuperAdmin(user) || isEnterpriseAdmin(user);
}

export function getScopedClientId(user: User | null | undefined): string | null {
  if (isSuperAdmin(user)) return null;
  if (isEnterpriseAdmin(user)) return (user?.customClaims?.clientId as string) ?? null;
  return null;
}
